import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useState } from 'react';
import { useSavedViews } from '../hooks/use-saved-views.js';
import type { SavedView, SavedViewState } from '../lib/saved-views-client.js';
import { ManageViewsDialog } from './manage-views-dialog.js';
import { SaveViewDialog } from './save-view-dialog.js';
import { SharedViewBadge } from './shared-view-badge.js';

interface SavedViewsMenuProps {
  tenantReportId: string;
  currentState: SavedViewState;
  canSaveSharedView: boolean;
  onLoad: (state: SavedViewState) => void;
}

export function SavedViewsMenu({
  tenantReportId,
  currentState,
  canSaveSharedView,
  onLoad,
}: SavedViewsMenuProps) {
  const [open, setOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);

  const { data } = useSavedViews(tenantReportId);
  const views = data?.items ?? [];

  const personal = views.filter((v) => v.scope === 'personal');
  const shared = views.filter((v) => v.scope === 'shared');

  function handleLoad(view: SavedView) {
    setOpen(false);
    onLoad(view.state);
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-[var(--radius-button)]',
          'border border-[var(--color-qup-border-dark)] px-3 py-2 text-sm font-medium',
          'text-[var(--color-qup-text-dark)] hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
        )}
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M2 4.5A1.5 1.5 0 013.5 3h13A1.5 1.5 0 0118 4.5v2A1.5 1.5 0 0116.5 8h-13A1.5 1.5 0 012 6.5v-2zM3.5 11a1.5 1.5 0 00-1.5 1.5v2A1.5 1.5 0 003.5 16h7a1.5 1.5 0 001.5-1.5v-2A1.5 1.5 0 0010.5 11h-7z" />
        </svg>
        {t('saved_views.menu_label')}
        <svg
          className={cn('h-3 w-3 transition-transform', open && 'rotate-180')}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop — button so keyboard-driven dismiss works */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: dismiss backdrop is intentionally mouse-only; Esc key handled at document level */}
          <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setOpen(false)} />

          {/* Dropdown panel */}
          <div
            role="menu"
            className={cn(
              'absolute right-0 z-20 mt-1 min-w-[220px] rounded-[var(--radius-card)]',
              'border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)]',
              'shadow-[var(--shadow-card)] py-1',
            )}
          >
            {/* Personal views */}
            {personal.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-qup-text-muted-dark)]">
                  {t('saved_views.personal')}
                </div>
                {personal.map((view) => (
                  <button
                    key={view.id}
                    type="button"
                    role="menuitem"
                    onClick={() => handleLoad(view)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm text-[var(--color-qup-text-dark)]',
                      'hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-100',
                      'focus-visible:outline-none focus-visible:bg-[var(--color-qup-bg-dark)]',
                    )}
                  >
                    {view.name}
                  </button>
                ))}
              </>
            )}

            {/* Shared views */}
            {shared.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-qup-text-muted-dark)]">
                  {t('saved_views.shared')}
                </div>
                {shared.map((view) => (
                  <button
                    key={view.id}
                    type="button"
                    role="menuitem"
                    onClick={() => handleLoad(view)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm text-[var(--color-qup-text-dark)]',
                      'hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-100',
                      'focus-visible:outline-none focus-visible:bg-[var(--color-qup-bg-dark)]',
                      'flex items-center justify-between gap-2',
                    )}
                  >
                    <span>{view.name}</span>
                    <SharedViewBadge />
                  </button>
                ))}
              </>
            )}

            {/* Empty state */}
            {views.length === 0 && (
              <div className="px-3 py-3 text-sm text-[var(--color-qup-text-muted-dark)]">
                {t('saved_views.empty')}
              </div>
            )}

            {/* Divider */}
            <div className="my-1 border-t border-[var(--color-qup-border-dark)]" />

            {/* Actions */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setSaveDialogOpen(true);
              }}
              className={cn(
                'w-full px-3 py-2 text-left text-sm text-[var(--color-qup-text-dark)]',
                'hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-100',
                'focus-visible:outline-none focus-visible:bg-[var(--color-qup-bg-dark)]',
              )}
            >
              {t('saved_views.save_current')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setManageDialogOpen(true);
              }}
              className={cn(
                'w-full px-3 py-2 text-left text-sm text-[var(--color-qup-text-dark)]',
                'hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-100',
                'focus-visible:outline-none focus-visible:bg-[var(--color-qup-bg-dark)]',
              )}
            >
              {t('saved_views.manage')}
            </button>
          </div>
        </>
      )}

      <SaveViewDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        tenantReportId={tenantReportId}
        currentState={currentState}
        canSaveSharedView={canSaveSharedView}
      />

      <ManageViewsDialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        tenantReportId={tenantReportId}
        canSaveSharedView={canSaveSharedView}
      />
    </div>
  );
}
